import { useEffect, useRef, useState } from 'react';
import '../styles/HowItWorks.css';

const STEPS = [
  {
    id: 1,
    title: 'Deposit in Doppler Vault',
    description: 'Users simply deposit crypto into Doppler Vaults.',
  },
  {
    id: 2,
    title: 'Asset Allocation',
    description:
      'Deposited assets are allocated across a variety of institutional-grade strategies, such as arbitrage and basis trading.',
  },
  {
    id: 3,
    title: 'Yield Generation & Distribution',
    description:
      "Yields are generated through multiple strategies and distributed to users. Yields automatically accrue and compound—no action required on user's part.",
  },
];

// Builds the 3 visible faces of a simple isometric cube/box centered at
// (cx, cy) with half-width hw and vertical height h. Reused for all three
// shapes in the diagram (Users cube, Vault cube, wide Strategies box).
function isoBox(cx, cy, hw, h) {
  const top = `M${cx},${cy - h / 2 - hw * 0.5} L${cx + hw},${cy - h / 2} L${cx},${cy - h / 2 + hw * 0.5} L${cx - hw},${cy - h / 2} Z`;
  const left = `M${cx - hw},${cy - h / 2} L${cx},${cy - h / 2 + hw * 0.5} L${cx},${cy + h / 2 + hw * 0.5} L${cx - hw},${cy + h / 2} Z`;
  const right = `M${cx},${cy - h / 2 + hw * 0.5} L${cx + hw},${cy - h / 2} L${cx + hw},${cy + h / 2} L${cx},${cy + h / 2 + hw * 0.5} Z`;
  return { top, left, right };
}

const USERS_BOX = isoBox(250, 110, 55, 80);
const VAULT_BOX = isoBox(250, 340, 68, 100);
const STRATEGY_BOX = isoBox(250, 590, 150, 60);

function Diagram({ activeStep }) {
  return (
    <svg
      className="how-diagram"
      viewBox="0 0 500 700"
      data-step={activeStep}
      role="img"
      aria-label="How Doppler Vaults route deposits into yield"
    >
      <defs>
        <pattern id="how-grid" width="24" height="24" patternUnits="userSpaceOnUse">
          <path d="M24 0H0V24" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        </pattern>
        <linearGradient id="how-strategy-gradient" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#7c5cff" />
          <stop offset="100%" stopColor="#3fe8d0" />
        </linearGradient>
      </defs>

      <rect x="0" y="0" width="500" height="700" fill="url(#how-grid)" />

      {/* deposit: Users -> Vault */}
      <line x1="250" y1="150" x2="250" y2="270" className="how-diagram__line how-diagram__line--deposit" />
      <polygon points="245,265 255,265 250,278" className="how-diagram__arrow how-diagram__line--deposit" />
      <text x="262" y="215" className="how-diagram__label how-diagram__line--deposit">deposit</text>

      {/* yields: Vault -> Users (feedback line, drawn slightly right) */}
      <line x1="290" y1="270" x2="290" y2="150" className="how-diagram__line how-diagram__line--yields-a" />
      <polygon points="285,155 295,155 290,142" className="how-diagram__arrow how-diagram__line--yields-a" />
      <text x="298" y="215" className="how-diagram__label how-diagram__line--yields-a">yields</text>

      {/* allocate: Vault -> Strategies */}
      <line x1="220" y1="410" x2="180" y2="520" className="how-diagram__line how-diagram__line--allocate" />
      <polygon points="176,513 186,517 178,527" className="how-diagram__arrow how-diagram__line--allocate" />
      <text x="130" y="470" className="how-diagram__label how-diagram__line--allocate">allocate</text>

      {/* yields: Strategies -> Vault */}
      <line x1="300" y1="520" x2="320" y2="410" className="how-diagram__line how-diagram__line--yields-b" />
      <polygon points="315,417 325,413 318,405" className="how-diagram__arrow how-diagram__line--yields-b" />
      <text x="330" y="470" className="how-diagram__label how-diagram__line--yields-b">yields</text>

      {/* Users cube */}
      <g className="how-diagram__shape how-diagram__shape--users">
        <path d={USERS_BOX.left} className="how-diagram__face how-diagram__face--left" />
        <path d={USERS_BOX.right} className="how-diagram__face how-diagram__face--right" />
        <path d={USERS_BOX.top} className="how-diagram__face how-diagram__face--top" />
        <text x="250" y="106" className="how-diagram__face-label">Users</text>
      </g>

      {/* Doppler Vault cube */}
      <g className="how-diagram__shape how-diagram__shape--vault">
        <path d={VAULT_BOX.left} className="how-diagram__face how-diagram__face--left" />
        <path d={VAULT_BOX.right} className="how-diagram__face how-diagram__face--right" />
        <path d={VAULT_BOX.top} className="how-diagram__face how-diagram__face--top" />
        <text x="250" y="330" className="how-diagram__face-label">Doppler</text>
        <text x="250" y="346" className="how-diagram__face-label">Vault</text>
      </g>

      {/* Strategies wide box */}
      <g className="how-diagram__shape how-diagram__shape--strategies">
        <path d={STRATEGY_BOX.left} className="how-diagram__face how-diagram__face--left" />
        <path d={STRATEGY_BOX.right} className="how-diagram__face how-diagram__face--right" />
        <path d={STRATEGY_BOX.top} className="how-diagram__face how-diagram__face--top" />
        <text x="160" y="584" className="how-diagram__face-label how-diagram__face-label--small">Arbitrage</text>
        <text x="250" y="576" className="how-diagram__face-label how-diagram__face-label--small">Basis Trading</text>
        <text x="340" y="568" className="how-diagram__face-label how-diagram__face-label--small">On-chain Strategies</text>
      </g>
    </svg>
  );
}

export default function HowItWorks({ zIndex }) {
  const [activeStep, setActiveStep] = useState(1);
  const cardRefs = useRef([]);
  const mobileTrackRef = useRef(null);

  // Desktop: whichever step card crosses the vertical center of the
  // viewport becomes active. rootMargin shrinks the trigger zone to a
  // thin band through the middle of the screen.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.stepIndex);
            setActiveStep(STEPS[idx].id);
          }
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Mobile: whichever card is mostly visible inside the swipeable track
// becomes active — this is what makes swiping (not just tapping dots)
// update the diagram's color.
useEffect(() => {
  const track = mobileTrackRef.current;
  if (!track) return;

  const cards = Array.from(track.children);
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
          const idx = cards.indexOf(entry.target);
          if (idx !== -1) setActiveStep(STEPS[idx].id);
        }
      });
    },
    { root: track, threshold: [0.6] }
  );

  cards.forEach((el) => observer.observe(el));
  return () => observer.disconnect();
}, []);

  function scrollMobileTo(index) {
    const track = mobileTrackRef.current;
    if (!track) return;
    const card = track.children[index];
    if (card) track.scrollTo({ left: card.offsetLeft - 16, behavior: 'smooth' });
    setActiveStep(STEPS[index].id);
  }

  return (
    <section className="how-it-works" style={{ zIndex }}>
      <div className="how-it-works__topbar">
        <span className="how-it-works__brand">Doppler.finance</span>
        <span className="how-it-works__pill">
          <span className="how-it-works__pill-brace">{'{'}</span>
          Doppler Vaults
          <span className="how-it-works__pill-brace">{'}'}</span>
        </span>
      </div>

      <h2 className="how-it-works__heading">
        <span className="how-it-works__bold">How</span> it works
      </h2>

      <div className="how-it-works__layout">
        {/* Desktop: stacked cards, one per step, scroll drives activeStep */}
        <div className="how-it-works__cards how-it-works__cards--desktop">
          {STEPS.map((step, i) => (
            <div
              key={step.id}
              ref={(el) => (cardRefs.current[i] = el)}
              data-step-index={i}
              className={`how-card ${activeStep === step.id ? 'how-card--active' : ''}`}
            >
              <span className="how-card__number">{step.id}</span>
              <h3 className="how-card__title">{step.title}</h3>
              <p className="how-card__desc">{step.description}</p>
            </div>
          ))}
        </div>

        <div className="how-it-works__diagram-wrap">
          <Diagram activeStep={activeStep} />
        </div>

        {/* Mobile: diagram on top, swipeable card carousel + dots below */}
        <div className="how-it-works__mobile-carousel">
          <div className="how-it-works__mobile-track" ref={mobileTrackRef}>
            {STEPS.map((step) => (
              <div key={step.id} className="how-card how-card--active how-card--mobile">
                <span className="how-card__number">{step.id}</span>
                <h3 className="how-card__title">{step.title}</h3>
                <p className="how-card__desc">{step.description}</p>
              </div>
            ))}
          </div>
          <div className="how-it-works__dots">
            {STEPS.map((step, i) => (
              <button
                key={step.id}
                type="button"
                aria-label={`Go to step ${step.id}`}
                className={`how-it-works__dot ${activeStep === step.id ? 'how-it-works__dot--active' : ''}`}
                onClick={() => scrollMobileTo(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}